import clsx from "clsx";
import React, { useEffect, useState } from "react";

export interface IStructuredTableSkeletonProps {
  columns?: number;
  rows?: number;
  viewportOffset?: number;
  rowHeight?: number;
  headerHeight?: number;
  bottomGutter?: number;
  minRows?: number;
  maxRows?: number;
  className?: string;
}

const DEFAULT_ROW_HEIGHT = 52;
const DEFAULT_HEADER_HEIGHT = 50;
const DEFAULT_BOTTOM_GUTTER = 24;

const StructuredTableSkeleton: React.FC<IStructuredTableSkeletonProps> = ({
  columns = 8,
  rows,
  viewportOffset = 260,
  rowHeight = DEFAULT_ROW_HEIGHT,
  headerHeight = DEFAULT_HEADER_HEIGHT,
  bottomGutter = DEFAULT_BOTTOM_GUTTER,
  minRows = 4,
  maxRows = 14,
  className,
}) => {
  const [autoRows, setAutoRows] = useState<number>(7);
  const safeColumns = Math.max(1, columns);
  const safeRowHeight = Math.max(1, rowHeight);
  const safeHeaderHeight = Math.max(1, headerHeight);
  const safeBottomGutter = Math.max(0, bottomGutter);
  const safeMinRows = Math.max(1, minRows);
  const safeMaxRows = Math.max(safeMinRows, maxRows);

  useEffect(() => {
    if (typeof window === "undefined" || rows !== undefined) return;

    const computeRows = () => {
      const viewportHeight = window.innerHeight;
      // Row count is based on viewport budget so it scales predictably across screens.
      const availableHeight = Math.max(280, viewportHeight - viewportOffset - safeBottomGutter);
      const computed = Math.floor((availableHeight - safeHeaderHeight) / safeRowHeight);
      const clamped = Math.min(safeMaxRows, Math.max(safeMinRows, computed));
      setAutoRows(clamped);
    };

    computeRows();
    window.addEventListener("resize", computeRows);
    return () => window.removeEventListener("resize", computeRows);
  }, [rows, viewportOffset, safeBottomGutter, safeHeaderHeight, safeRowHeight, safeMinRows, safeMaxRows]);

  const safeRows = Math.max(1, rows ?? autoRows);

  return (
    <div
      className={clsx(
        "relative rounded-[20px] bg-[#F9FAFB] overflow-hidden border border-[#D7D7D7] mr-[10px] animate-pulse",
        className,
      )}
    >
      <div className="border-b border-[#D7D7D7] bg-[#F6F7F8] px-[10px]" style={{ height: safeHeaderHeight }}>
        <div
          className="grid h-full gap-3 items-center"
          style={{ gridTemplateColumns: `repeat(${safeColumns}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: safeColumns }).map((_, idx) => (
            <div key={`header:${idx}`} className="h-3 rounded bg-gray-200" />
          ))}
        </div>
      </div>

      <div className="bg-white">
        {Array.from({ length: safeRows }).map((_, rowIdx) => (
          <div
            key={`row:${rowIdx}`}
            className="border-b border-[#D7D7D7] px-[10px] last:border-b-0"
            style={{ height: safeRowHeight }}
          >
            <div
              className="grid h-full gap-3 items-center"
              style={{ gridTemplateColumns: `repeat(${safeColumns}, minmax(0, 1fr))` }}
            >
              {Array.from({ length: safeColumns }).map((__, colIdx) => (
                <div
                  key={`cell:${rowIdx}:${colIdx}`}
                  className={clsx(
                    "h-3 rounded bg-gray-200",
                    colIdx % 3 === 0 ? "w-[60%]" : colIdx % 3 === 1 ? "w-[75%]" : "w-[45%]",
                  )}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StructuredTableSkeleton;
